// Plan hierarchy for upgrade/downgrade logic
export const PLAN_HIERARCHY = {
  free: 0,
  hs60: 1,
  hs90: 2,
  hs130: 3,
  hs240: 4
};

export function isUpgrade(currentPlan: string, newPlan: string): boolean {
  return PLAN_HIERARCHY[newPlan as keyof typeof PLAN_HIERARCHY] > PLAN_HIERARCHY[currentPlan as keyof typeof PLAN_HIERARCHY];
}

export function isDowngrade(currentPlan: string, newPlan: string): boolean {
  return PLAN_HIERARCHY[newPlan as keyof typeof PLAN_HIERARCHY] < PLAN_HIERARCHY[currentPlan as keyof typeof PLAN_HIERARCHY];
}

export function hasPlanOrHigher(athletePlan: string, requiredPlan: string): boolean {
  return PLAN_HIERARCHY[athletePlan as keyof typeof PLAN_HIERARCHY] >= PLAN_HIERARCHY[requiredPlan as keyof typeof PLAN_HIERARCHY];
}

type SubscriptionLike = {
  plan?: string;
  subscriptionStatus?: 'free' | 'active' | 'past_due' | 'canceled' | 'trialing' | string;
  subscriptionEndDate?: string | Date | null;
  nextBillingDate?: string | Date | null;
  cancelAtPeriodEnd?: boolean;
};

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Determines whether a subscription currently grants access ("entitlement").
 *
 * Important: the UI may keep `plan` set (e.g. `hs60`) even after cancellation;
 * entitlement must also consider status + end date.
 */
export function isSubscriptionEntitled(subscription?: SubscriptionLike | null): boolean {
  if (!subscription) return false;

  const plan = subscription.plan || 'free';
  if (plan === 'free') return false;

  const status = subscription.subscriptionStatus || 'free';
  if (status === 'free' || status === 'canceled') return false;

  // If an end date exists and is in the past, it no longer grants access.
  const endDate = toDate(subscription.subscriptionEndDate);
  if (endDate && endDate.getTime() <= Date.now()) return false;

  // For active-ish statuses, allow access. (past_due is still treated as paid in current backend logic)
  return status === 'active' || status === 'trialing' || status === 'past_due';
}

// Page requirements mapping
export const PAGE_REQUIREMENTS: Record<string, 'free' | 'hs60' | 'hs90' | 'hs130' | 'hs240'> = {
  'home': 'free',
  'athletes': 'free',
  'training-sessions': 'free',
  'competition-sessions': 'hs60',
  'equipment': 'hs60',
  'training-planner': 'hs90',
  'banana-cup': 'hs90',  // Banana Cup
  'finances': 'hs90',  // Banana Cup (alternative name)
  'analytics': 'hs130',
  'analytics/home': 'hs130',
  'analytics/distance': 'hs130',
  'analytics/equipment': 'hs130',
  'analytics/weight': 'hs130',
  'analytics/inrun-speed': 'hs130',
  'analytics/rating-form': 'hs130',
  'analytics/gate-number': 'hs130',
  'analytics/training-sessions': 'hs130',
  'analytics/competition-sessions': 'hs130',
  'analytics/ground-training': 'hs130',
  'analytics/cross-country': 'hs130',
  'ai-chat': 'hs240'
};

/**
 * Filter athletes based on required plan for a page
 */
export function filterAthletesByPlan<T extends { subscription?: { plan?: string | 'free' | 'hs60' | 'hs90' | 'hs130' | 'hs240' } }>(
  athletes: T[],
  requiredPlan: 'free' | 'hs60' | 'hs90' | 'hs130' | 'hs240'
): T[] {
  if (requiredPlan === 'free') {
    return athletes; // All athletes accessible
  }
  
  const filtered = athletes.filter(athlete => {
    const athletePlan = (athlete.subscription?.plan as string) || 'free';
    return hasPlanOrHigher(athletePlan, requiredPlan);
  });
  
  return filtered as T[];
}

/**
 * Get required plan for a page path
 */
export function getRequiredPlan(pagePath: string): 'free' | 'hs60' | 'hs90' | 'hs130' | 'hs240' {
  return PAGE_REQUIREMENTS[pagePath] || 'free';
}

